import { useMemo } from "react";
import { safeGetJSON } from "@/utils/safeStorage";

const FALLBACK_STYLES: Record<string, string> = {
  "--backgroundColor": "#12322b",
  "--color5": "rgba(255, 255, 255, 0.05)",
  "--color4": "rgba(255, 255, 255, 0.08)",
  "--logoColor": "#d7f3e8",
  "--logoColor2": "#ffffff",
  "--sidebarColor": "#174036",
};

interface LoaderThemeOptions {
  neutral?: boolean;
}

const readThemeValue = (name: string) => {
  if (typeof window === "undefined") return FALLBACK_STYLES[name];

  const rootValue = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (rootValue) return rootValue;

  const cached = safeGetJSON<Record<string, string>>("processedStyles");
  return (cached && typeof cached === "object" && cached[name]) || FALLBACK_STYLES[name];
};

const getNeutralLoaderTheme = () => {
  const prefersLight =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: light)").matches;

  if (prefersLight) {
    return {
      background: "#ffffff",
      nav: "#ffffff",
      skeletonBase: "rgba(0, 0, 0, 0.08)",
      skeletonHighlight: "rgba(0, 0, 0, 0.14)",
      accent: "#000000",
      text: "#000000",
      muted: "rgba(0, 0, 0, 0.62)",
    };
  }

  return {
    background: "#000000",
    nav: "#000000",
    skeletonBase: "rgba(255, 255, 255, 0.08)",
    skeletonHighlight: "rgba(255, 255, 255, 0.14)",
    accent: "#ffffff",
    text: "#ffffff",
    muted: "rgba(255, 255, 255, 0.68)",
  };
};

export function useLoaderTheme(options?: LoaderThemeOptions) {
  return useMemo(() => {
    if (options?.neutral) return getNeutralLoaderTheme();

    const background = readThemeValue("--backgroundColor");
    const surface = readThemeValue("--color5");
    const surfaceStrong = readThemeValue("--color4");
    const accent = readThemeValue("--logoColor2");

    return {
      background,
      nav: readThemeValue("--sidebarColor"),
      skeletonBase: surface || "rgba(255, 255, 255, 0.05)",
      skeletonHighlight: surfaceStrong || "rgba(255, 255, 255, 0.08)",
      accent,
      text: accent,
      muted: accent,
    };
  }, [options?.neutral]);
}
